import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    ReactNode,
} from 'react';
import { CampaignContextType, CouponData } from '../types/campaign';

const CampaignContext = createContext<CampaignContextType | undefined>(
    undefined
);

interface CampaignProviderProps {
    children: ReactNode;
}

export function CampaignProvider({ children }: CampaignProviderProps) {
    // Coupon data state
    const [couponData, setCouponData] = useState<CouponData | null>(null);

    // Original coupon code from query parameter
    const [originalCouponCode, setOriginalCouponCode] = useState<string | null>(
        null
    );

    // Campaign state
    const [fromCampaign, setFromCampaign] = useState<boolean>(false);

    // Modal state
    const [showCampaignModal, setShowCampaignModal] = useState<boolean>(false);

    // Track if modal has been shown this session (to prevent reopening)
    // Initialize from sessionStorage keyed by coupon code
    const [hasShownModal, setHasShownModalState] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            const couponCode = new URLSearchParams(window.location.search).get(
                'coupon'
            );
            if (couponCode) {
                const key = `campaign_modal_shown_${couponCode}`;
                return sessionStorage.getItem(key) === 'true';
            }
        }
        return false;
    });

    // Wrapper that persists to sessionStorage when setting hasShownModal to true
    const setHasShownModal = useCallback((value: boolean) => {
        setHasShownModalState(value);
        if (typeof window !== 'undefined' && value) {
            const couponCode = new URLSearchParams(window.location.search).get(
                'coupon'
            );
            if (couponCode) {
                const key = `campaign_modal_shown_${couponCode}`;
                sessionStorage.setItem(key, 'true');
            }
        }
    }, []);

    // Loading state
    const [isLoadingCoupon, setIsLoadingCoupon] = useState<boolean>(false);

    // Error state
    const [couponError, setCouponError] = useState<string | null>(null);

    // Helper method to clear all campaign data
    const clearCampaignData = useCallback(() => {
        setCouponData(null);
        setOriginalCouponCode(null);
        setFromCampaign(false);
        setShowCampaignModal(false);
        setIsLoadingCoupon(false);
        setCouponError(null);
        setHasShownModalState(false);
        // Also clear sessionStorage entry for current coupon
        if (typeof window !== 'undefined') {
            const couponCode = new URLSearchParams(window.location.search).get(
                'coupon'
            );
            if (couponCode) {
                sessionStorage.removeItem(`campaign_modal_shown_${couponCode}`);
            }
        }
    }, []);

    // Helper method to check if we have campaign data
    const hasCampaignData = useCallback(() => {
        return !!(couponData && fromCampaign);
    }, [couponData, fromCampaign]);

    const contextValue: CampaignContextType = {
        // Coupon data
        couponData,
        setCouponData,
        originalCouponCode,
        setOriginalCouponCode,

        // Campaign state
        fromCampaign,
        setFromCampaign,

        // Modal state
        showCampaignModal,
        setShowCampaignModal,
        hasShownModal,
        setHasShownModal,

        // Loading state
        isLoadingCoupon,
        setIsLoadingCoupon,

        // Error state
        couponError,
        setCouponError,

        // Helper methods
        clearCampaignData,
        hasCampaignData,
    };

    return (
        <CampaignContext.Provider value={contextValue}>
            {children}
        </CampaignContext.Provider>
    );
}

// Custom hook to use the campaign context
export function useCampaign(): CampaignContextType {
    const context = useContext(CampaignContext);

    if (context === undefined) {
        throw new Error('useCampaign must be used within a CampaignProvider');
    }

    return context;
}

export default CampaignContext;
